"use client"

import { useEffect } from "react"

import { useRouter } from "next/navigation"

import { LOCALE_COOKIE, resolveLocale, type Locale } from "@/i18n/locales"
import { canonicalPrefix, splitLocalePrefix } from "@/i18n/routing"
import { readColorScheme } from "@/lib/color-scheme/scheme"
import { pathWithQuery } from "@/lib/routes"
import { SAFE_AREA_VARIABLES, readSafeArea } from "@/platform/app-mode/client"
import { applyColorScheme } from "@/platform/app-mode/color-scheme"
import { useNavigationPath } from "@/platform/app-mode/NavigationProvider"
import { useNativeBridge } from "@/platform/bridge/use-native-bridge"
import { setCookie } from "@/platform/browser/cookies"

const LOCALE_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365

const BRIDGE_LOCALES: Readonly<Record<string, Locale>> = { en: "en-US", es: "es-MX" }

const readLocale = (data: Record<string, unknown>): Locale | undefined => {
  const candidate = data.locale
  const locale = typeof candidate === "string" ? BRIDGE_LOCALES[candidate] : undefined
  return locale && resolveLocale(locale)
}

const rememberLocale = (locale: Locale): void => setCookie(LOCALE_COOKIE, locale, { maxAgeSeconds: LOCALE_COOKIE_MAX_AGE_SECONDS, sameSite: "Lax" })

export const AppModeProps = () => {
  const bridge = useNativeBridge()
  const router = useRouter()
  const navigationPath = useNavigationPath()

  useEffect(() => {
    if (!bridge.available) return

    return bridge.on("props", (data) => {
      const safeArea = readSafeArea(data)
      if (safeArea) {
        for (const edge of Object.keys(SAFE_AREA_VARIABLES) as Array<keyof typeof SAFE_AREA_VARIABLES>) {
          document.documentElement.style.setProperty(SAFE_AREA_VARIABLES[edge], `${safeArea[edge]}px`)
        }
      }

      const colorScheme = readColorScheme(data)
      if (colorScheme) applyColorScheme(colorScheme)

      const locale = readLocale(data)
      if (locale === undefined) return

      rememberLocale(locale)

      if (resolveLocale(document.documentElement.lang) === locale) return

      const { rest } = splitLocalePrefix(window.location.pathname)
      router.replace(navigationPath(pathWithQuery(`${canonicalPrefix(locale)}${rest}`, window.location.search.slice(1))))
    })
  }, [bridge, router, navigationPath])

  return null
}
